import { db, rtdb, fns } from '../firebaseConfig';
import {
  collection,
  doc,
  getDoc,
  getDocs,
  onSnapshot,
  query,
  orderBy,
  limit,
  where,
  setDoc,
  updateDoc,
  serverTimestamp,
} from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import {
  ref as rtdbRef,
  set as rtdbSet,
  onDisconnect,
  serverTimestamp as rtdbServerTimestamp,
} from 'firebase/database';

// ─── Firestore paths ──────────────────────────────────────────────────────────
//
//  communities/{communityId}
//    categories/{categoryId}          ← ordered category groups
//      channels/{channelId}           ← text/voice channels
//        subChannels/{subChannelId}   ← optional sub-channels
//
//  communities/{communityId}/members/{uid}
//    { uid, username, displayName, photoURL, role, onlineStatus, lastSeen }
//

// ─── Channel categories & channels ───────────────────────────────────────────

/**
 * Fetch all categories with their channels (and sub-channels) for a community.
 * Returns them sorted by `order` field.
 *
 * @param {string} communityId
 * @returns {Promise<{ categories: Array, error: string|null }>}
 */
export async function getChannelCategories(communityId) {
  try {
    const categoriesRef = collection(db, 'communities', communityId, 'categories');
    const categoriesSnap = await getDocs(query(categoriesRef, orderBy('order')));

    const categories = await Promise.all(
      categoriesSnap.docs.map(async (categoryDoc) => {
        const category = { id: categoryDoc.id, ...categoryDoc.data() };

        const channelsRef = collection(
          db, 'communities', communityId, 'categories', category.id, 'channels'
        );
        const channelsSnap = await getDocs(query(channelsRef, orderBy('order')));

        category.channels = await Promise.all(
          channelsSnap.docs.map(async (channelDoc) => {
            const channel = { id: channelDoc.id, ...channelDoc.data() };

            const subRef = collection(
              db,
              'communities', communityId,
              'categories', category.id,
              'channels', channel.id,
              'subChannels'
            );
            const subSnap = await getDocs(query(subRef, orderBy('order')));
            channel.subChannels = subSnap.docs.map((s) => ({ id: s.id, ...s.data() }));

            return channel;
          })
        );

        return category;
      })
    );

    return { categories, error: null };
  } catch (error) {
    console.error('[communityService] getChannelCategories:', error);
    return { categories: [], error: error.message };
  }
}

/**
 * Subscribe to real-time category/channel updates.
 * Only watches the categories level; call getChannelCategories for a full refresh
 * when the snapshot fires if deep nesting is needed.
 *
 * @param {string} communityId
 * @param {(categories: Array) => void} onUpdate
 * @returns {() => void} unsubscribe function
 */
export function subscribeToChannels(communityId, onUpdate) {
  const categoriesRef = collection(db, 'communities', communityId, 'categories');
  return onSnapshot(query(categoriesRef, orderBy('order')), async () => {
    const { categories } = await getChannelCategories(communityId);
    onUpdate(categories);
  });
}

// ─── Channel info ─────────────────────────────────────────────────────────────

/**
 * Fetch metadata for a single channel.
 *
 * @param {string} communityId
 * @param {string} categoryId
 * @param {string} channelId
 * @returns {Promise<{ channel: Object|null, error: string|null }>}
 */
export async function getChannelInfo(communityId, categoryId, channelId) {
  try {
    const channelRef = doc(
      db, 'communities', communityId, 'categories', categoryId, 'channels', channelId
    );
    const snap = await getDoc(channelRef);

    if (!snap.exists()) {
      return { channel: null, error: 'Channel not found' };
    }

    return { channel: { id: snap.id, ...snap.data() }, error: null };
  } catch (error) {
    console.error('[communityService] getChannelInfo:', error);
    return { channel: null, error: error.message };
  }
}

/**
 * Subscribe to real-time updates for a single channel's metadata.
 *
 * @param {string} communityId
 * @param {string} categoryId
 * @param {string} channelId
 * @param {(channel: Object) => void} onUpdate
 * @returns {() => void} unsubscribe function
 */
export function subscribeToChannelInfo(communityId, categoryId, channelId, onUpdate) {
  const channelRef = doc(
    db, 'communities', communityId, 'categories', categoryId, 'channels', channelId
  );
  return onSnapshot(channelRef, (snap) => {
    if (snap.exists()) {
      onUpdate({ id: snap.id, ...snap.data() });
    }
  });
}

// ─── Members ──────────────────────────────────────────────────────────────────

/**
 * Fetch all members for a community.
 *
 * @param {string} communityId
 * @returns {Promise<{ members: Array, error: string|null }>}
 */
export async function getCommunityMembers(communityId) {
  try {
    const membersRef = collection(db, 'communities', communityId, 'members');
    const snap = await getDocs(membersRef);
    const members = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
    return { members, error: null };
  } catch (error) {
    console.error('[communityService] getCommunityMembers:', error);
    return { members: [], error: error.message };
  }
}

/**
 * Subscribe to real-time member presence (online/offline status).
 *
 * Watches `communities/{communityId}/members` for live role/status changes,
 * then enriches each entry with profile fields from the top-level `users`
 * collection: displayName, photoURL, profilePhoto, username, email.
 *
 * @param {string} communityId
 * @param {(members: Array) => void} onUpdate
 * @returns {() => void} unsubscribe function
 */
export function subscribeToMemberPresence(_communityId, onUpdate) {
  // Show all platform users by default (community members sub-collection is not used for display)
  const usersRef = collection(db, 'users');

  return onSnapshot(usersRef, (snap) => {
    if (snap.empty) {
      onUpdate([]);
      return;
    }

    const members = snap.docs.map((userDoc) => {
      const { username, email, profilePhoto, photoURL, subscription } = userDoc.data();
      const displayName = username || (email ? email.split('@')[0] : null);
      return {
        id: userDoc.id,
        uid: userDoc.id,
        onlineStatus: null,
        lastSeen: null,
        joinedAt: null,
        displayName,
        photoURL: photoURL ?? profilePhoto ?? null,
        username: username ?? null,
        email: email ?? null,
        subscription: subscription ?? null,
      };
    });

    onUpdate(members);
  });
}

/**
 * Fetch only online members for a community.
 *
 * @param {string} communityId
 * @returns {Promise<{ members: Array, error: string|null }>}
 */
export async function getOnlineMembers(communityId) {
  try {
    const membersRef = collection(db, 'communities', communityId, 'members');
    const snap = await getDocs(query(membersRef, where('onlineStatus', '==', 'online')));
    const members = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
    return { members, error: null };
  } catch (error) {
    console.error('[communityService] getOnlineMembers:', error);
    return { members: [], error: error.message };
  }
}

/**
 * Update the current user's presence status.
 *
 * @param {string} communityId
 * @param {string} uid
 * @param {'online'|'offline'|'away'} status
 * @returns {Promise<{ error: string|null }>}
 */
export async function setMemberPresence(communityId, uid, status) {
  try {
    const memberRef = doc(db, 'communities', communityId, 'members', uid);
    await updateDoc(memberRef, {
      onlineStatus: status,
      lastSeen: serverTimestamp(),
    });
    return { error: null };
  } catch (error) {
    console.error('[communityService] setMemberPresence:', error);
    return { error: error.message };
  }
}

/**
 * Initialize crash-safe presence via Firebase Realtime Database.
 *
 * Writes `online` immediately, then registers an `onDisconnect` handler so
 * Firebase automatically writes `offline` if the client loses its connection
 * (crash, network drop, forced close) — something Firestore cannot do alone.
 *
 * A Cloud Function (`mirrorPresenceToFirestore`) watches the RTDB path
 * `community_presence/{communityId}/{uid}` and mirrors every change into
 * `communities/{communityId}/members/{uid}` in Firestore.
 *
 * @param {string} communityId
 * @param {string} uid
 * @returns {Promise<{ cleanup: () => void, error: string|null }>}
 *   `cleanup` should be called on unmount to mark the user offline immediately
 *   (rather than waiting for the socket to timeout).
 */
export async function initPresence(communityId, uid) {
  if (!rtdb) {
    // Realtime Database not configured — presence feature unavailable
    return { cleanup: () => {}, error: null };
  }
  try {
    const presenceRef = rtdbRef(rtdb, `community_presence/${communityId}/${uid}`);

    // Register the disconnect handler BEFORE writing online so there is no
    // window where the node is online but the handler is not yet registered.
    await onDisconnect(presenceRef).set({
      status: 'offline',
      lastSeen: rtdbServerTimestamp(),
    });

    await rtdbSet(presenceRef, {
      status: 'online',
      lastSeen: rtdbServerTimestamp(),
    });

    const cleanup = () =>
      rtdbSet(presenceRef, {
        status: 'offline',
        lastSeen: rtdbServerTimestamp(),
      });

    return { cleanup, error: null };
  } catch (error) {
    console.error('[communityService] initPresence:', error);
    return { cleanup: () => {}, error: error.message };
  }
}

// ─── Messages ─────────────────────────────────────────────────────────────────

/**
 * Send a message to a channel.
 *
 * @param {string} communityId
 * @param {string} categoryId
 * @param {string} channelId
 * @param {{ text?: string, attachments?: Array, videoUrl?: string, uid?: string, username?: string, displayName?: string, photoURL?: string }} messageData
 *   Identity fields are accepted for caller compatibility, but server auth is authoritative.
 * @returns {Promise<{ messageId: string|null, flagged: boolean, blocked: boolean, error: string|null }>}
 */
export async function sendMessage(communityId, categoryId, channelId, messageData) {
  if (!fns) {
    return { messageId: null, error: 'Messaging service not available', blocked: false, flagged: false };
  }
  try {
    const communitySendMessage = httpsCallable(fns, 'communitySendMessage');
    const result = await communitySendMessage({ communityId, categoryId, channelId, messageData });
    return { messageId: result.data.messageId, flagged: result.data.flagged, error: null, blocked: false };
  } catch (error) {
    console.error('[communityService] sendMessage:', error);
    const blocked = error?.code === 'functions/permission-denied';
    return { messageId: null, error: error.message, blocked, flagged: false };
  }
}

/**
 * Fetch recent messages for a channel (newest last).
 *
 * @param {string} communityId
 * @param {string} categoryId
 * @param {string} channelId
 * @param {number} [maxMessages=50]
 * @returns {Promise<{ messages: Array, error: string|null }>}
 */
export async function getMessages(communityId, categoryId, channelId, maxMessages = 50) {
  try {
    const messagesRef = collection(
      db,
      'communities', communityId,
      'categories', categoryId,
      'channels', channelId,
      'messages'
    );
    const snap = await getDocs(
      query(messagesRef, orderBy('createdAt', 'asc'), limit(maxMessages))
    );
    const messages = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
    return { messages, error: null };
  } catch (error) {
    console.error('[communityService] getMessages:', error);
    return { messages: [], error: error.message };
  }
}

/**
 * Subscribe to real-time message updates for a channel.
 *
 * @param {string} communityId
 * @param {string} categoryId
 * @param {string} channelId
 * @param {(messages: Array) => void} onUpdate
 * @param {number} [maxMessages=50]
 * @returns {() => void} unsubscribe function
 */
export function subscribeToMessages(communityId, categoryId, channelId, onUpdate, maxMessages = 50) {
  const messagesRef = collection(
    db,
    'communities', communityId,
    'categories', categoryId,
    'channels', channelId,
    'messages'
  );
  return onSnapshot(
    query(messagesRef, orderBy('createdAt', 'asc'), limit(maxMessages)),
    (snap) => {
      const messages = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
      onUpdate(messages);
    },
    (error) => {
      console.error('[communityService] subscribeToMessages:', error);
    }
  );
}

/**
 * Register a user as a member of a community (upsert).
 *
 * @param {string} communityId
 * @param {{ uid: string, username: string, displayName?: string, photoURL?: string, role?: string }} memberData
 * @returns {Promise<{ error: string|null }>}
 */
export async function joinCommunity(communityId, memberData) {
  try {
    const memberRef = doc(db, 'communities', communityId, 'members', memberData.uid);
    await setDoc(
      memberRef,
      {
        ...memberData,
        role: memberData.role || 'member',
        onlineStatus: 'online',
        joinedAt: serverTimestamp(),
        lastSeen: serverTimestamp(),
      },
      { merge: true }
    );
    return { error: null };
  } catch (error) {
    console.error('[communityService] joinCommunity:', error);
    return { error: error.message };
  }
}
